import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { authStore } from '../../api/client';
import {
  getCandidateDashboard, listCandidateApplications, listCandidateInterviews,
} from '../../api/candidatePortal';
import { getFitVerdictChip } from '../../utils/formatters';
import FeedbackBody from '../components/FeedbackBody';
import { getAgentDownloads } from '../../api/agent';
import StatCard from '../components/StatCard';
import {
  PROBE_INTERVAL, canJoinScheduledInterview, detectPlatform, probeAgent,
} from '../utils/agentProbe';

export default function CandidateDashboardPage() {
  const [dashboard, setDashboard] = useState(null);
  const [counts, setCounts] = useState({ applications: null, interviewsCompleted: null });
  const [agent, setAgent] = useState(null);
  const [downloads, setDownloads] = useState({ platforms: {}, version: '' });
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const platform = useMemo(detectPlatform, []);
  const user = authStore.user;

  const load = useCallback(async () => {
    try {
      const [res, appsRes, ivRes] = await Promise.all([
        getCandidateDashboard(),
        listCandidateApplications().catch(() => null),
        listCandidateInterviews().catch(() => null),
      ]);
      setDashboard(res.data);
      // Real counts only; null (shown as "-") when a list could not be loaded.
      setCounts({
        applications: Array.isArray(appsRes?.data) ? appsRes.data.length : null,
        interviewsCompleted: Array.isArray(ivRes?.data)
          ? ivRes.data.filter((iv) => iv.status === 'ENDED').length
          : null,
      });
    } catch (err) {
      console.warn('[CandidateDashboard] load failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The help chatbot can reschedule an interview; refresh the next-interview card when it does.
  useEffect(() => {
    const onChange = () => load();
    window.addEventListener('samastix:schedules-changed', onChange);
    return () => window.removeEventListener('samastix:schedules-changed', onChange);
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    let timer;
    const tick = async () => {
      const result = await probeAgent();
      if (!cancelled) setAgent(result);
      timer = setTimeout(tick, PROBE_INTERVAL);
    };
    tick();
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    getAgentDownloads()
      .then((res) => setDownloads(res.data || { platforms: {}, version: '' }))
      .catch(() => {});
  }, []);

  const schedule = dashboard?.nextInterview;
  const joinGate = canJoinScheduledInterview(schedule);
  const canOpen = schedule?.status === 'ACTIVE' || joinGate.ok;

  const fmtCountdown = (target) => {
    if (!target) return '';
    const ms = new Date(target).getTime() - now;
    if (ms <= 0) return 'Ready to join';
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 48) return `Starts in ${Math.round(h / 24)} days`;
    if (h > 0) return `Starts in ${h}h ${m}m`;
    return `Starts in ${m}m ${s % 60}s`;
  };

  const openApp = () => {
    if (!schedule || !canOpen) return;
    const token = schedule.inviteToken
      || (schedule.inviteDeepLink || '').split('invite=')[1];
    if (token) {
      window.location.href = `samastix://join?invite=${encodeURIComponent(token)}`;
    }
  };

  const firstName = user?.fullName
    ? user.fullName.trim().split(' ')[0]
    : user?.email?.split('@')[0] || 'Candidate';

  const profileStrength = dashboard?.hasResume ? 78 : 35;

  if (loading) {
    return (
      <div className="py-24 text-center">
        <div className="w-8 h-8 border-2 border-[var(--teal-500)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-[var(--ink-soft)]">Loading your dashboard…</p>
      </div>
    );
  }

  // Latest feedback is exactly what the organization sent (no sample text, no
  // verdict derived from a score).
  const feedbackData = dashboard?.latestFeedback;
  // Practice feedback is coaching: never a fit / not-fit chip.
  const feedbackChip = feedbackData?.overallVerdict && feedbackData?.mode !== 'PRACTICE'
    ? getFitVerdictChip(feedbackData.overallVerdict) : null;
  const latestScore = dashboard?.latestInterviewScore;

  return (
    <div className="space-y-8">
      {/* ── Page Header ─────────────────────────────────────────────── */}
      <div>
        <h1
          className="text-2xl sm:text-[26px] font-semibold text-[var(--navy-950)] tracking-tight"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          Welcome back, {firstName}
        </h1>
        <p className="text-[13.5px] text-[var(--ink-soft)] mt-1">
          Here's where things stand across your applications and interviews.
        </p>
      </div>

      {/* ── 4 Stat Cards Row ────────────────────────────────────────── */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          icon={(
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          )}
          value={counts.applications}
          label="Applications"
          to="/candidate/applications"
        />

        <StatCard
          icon={(
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          )}
          value={counts.interviewsCompleted}
          label={schedule ? 'Interviews completed · 1 upcoming' : 'Interviews completed'}
          to="/candidate/interviews"
        />

        <StatCard
          icon={(
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
              <polyline points="16 7 22 7 22 13" />
            </svg>
          )}
          value={latestScore != null ? (
            <span className="font-tabular">
              {Math.round(latestScore)}
              <span className="text-base font-normal text-[var(--ink-faint)]">/100</span>
            </span>
          ) : '-'}
          label="Latest interview score"
          to="/candidate/interviews"
        />

        <StatCard
          icon={(
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          )}
          value={`${profileStrength}%`}
          label="Profile strength"
          progress={profileStrength}
          to="/candidate/profile"
        />
      </div>

      {/* ── Mid Section: Next Interview & Latest Feedback ─────────────── */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Next Interview Panel (real schedule only) */}
        {!schedule ? (
          <div className="card p-6 flex flex-col justify-center">
            <h3
              className="text-base font-semibold text-[var(--navy-950)]"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              No upcoming interview
            </h3>
            <p className="text-xs text-[var(--ink-soft)] mt-1">
              When an organization schedules an interview with you, it will appear here.
            </p>
          </div>
        ) : (
        <div className="card p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3
                  className="text-base font-semibold text-[var(--navy-950)]"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {schedule.title || 'Interview'}
                </h3>
                <p className="text-xs text-[var(--ink-soft)] mt-0.5">
                  {schedule.orgName || schedule.hostName || ''}
                </p>
              </div>
              <span className="chip chip-teal-solid">
                {schedule.status === 'ACTIVE' ? 'Live now' : 'Scheduled'}
              </span>
            </div>

            {/* Pill Container for session details */}
            <div className="mt-4 rounded-[12px] bg-[var(--teal-050)] border border-[var(--teal-100)] p-3.5 flex flex-wrap items-center gap-y-2 gap-x-4 text-xs text-[var(--teal-700)] font-medium">
              <div className="flex items-center gap-1.5">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <span>
                  {schedule?.scheduledAt
                    ? new Date(schedule.scheduledAt).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                      })
                    : 'Time to be confirmed'}
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span>{fmtCountdown(schedule?.scheduledAt)}</span>
              </div>

              <div className="flex items-center gap-1.5">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span>{agent ? 'Desktop agent detected' : 'Desktop agent required'}</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-6 flex items-center gap-3">
            <button
              type="button"
              onClick={openApp}
              className="btn btn-primary"
            >
              {schedule?.status === 'ACTIVE' ? 'Join live' : 'Join when it\'s time'}
            </button>
          </div>
        </div>
        )}

        {/* Latest Feedback Panel (exactly what the organization sent) */}
        <div className="card p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3
                className="text-base font-semibold text-[var(--navy-950)]"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Latest interview feedback
              </h3>
              {feedbackChip && (
                <span className={`chip ${feedbackChip.chipClass}`}>
                  {feedbackChip.label}
                </span>
              )}
            </div>
            {feedbackData ? (
              <div className="mt-3">
                <FeedbackBody feedback={feedbackData} compact />
              </div>
            ) : (
              <p className="mt-3 text-xs text-[var(--ink-soft)]">
                No feedback yet. Feedback appears here if the organization shares it after an interview.
              </p>
            )}
          </div>

          <div className="mt-5 pt-3 border-t border-[var(--line-soft)]">
            <Link
              to="/candidate/interviews"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--teal-600)] hover:text-[var(--teal-700)] no-underline"
            >
              <span>View all interviews</span>
              <span>&rarr;</span>
            </Link>
          </div>
        </div>
      </div>

      {/* ── OPEN ROLES (real openings live on the Openings page) ───────── */}
      <div>
        <p className="eyebrow mb-3">Open roles</p>
        <Link
          to="/candidate/openings"
          className="card p-5 card-hover no-underline text-left block"
        >
          <h4
            className="text-sm font-semibold text-[var(--navy-950)]"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Browse open roles
          </h4>
          <p className="text-xs text-[var(--ink-soft)] mt-0.5">
            See roles organizations are hiring for and apply with your profile.
          </p>
        </Link>
      </div>
    </div>
  );
}
